import './BankConnection.css';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl, envConfig, getCurrentEnv } from '../config/envConfig';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import TimerIcon from '../assets/icons/timer.js';

const SESSION_DURATION = 180; // 3 minutes in seconds
const QR_REFRESH_INTERVAL = 30; // 30 seconds

function BankConnection({ bankName = "Swedbank" }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [bankingInfo, setBankingInfo] = useState({ country: null, bank: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [consentStatus, setConsentStatus] = useState('pending');
    const statusMessageRef = useRef(t('bankConnStatusWaiting'));
    const scaStatusRef = useRef('');
    const [sessionExpired, setSessionExpired] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(SESSION_DURATION);
    const [qrCountdown, setQrCountdown] = useState(QR_REFRESH_INTERVAL);
    const pollingTimeoutRef = useRef(null);
    const isCreatingConsentRef = useRef(false);
    const isPollingRef = useRef(false);
    const initialQrSourceRef = useRef(null);
    const [userIp, setUserIp] = useState('');
    const [userAgent, setUserAgent] = useState('');
    const sessionTimerRef = useRef(null);
    const qrRefreshTimerRef = useRef(null);
    const consentDataRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        isPollingRef.current = false;
    }, []);

    const stopTimers = useCallback(() => {
        if (sessionTimerRef.current) {
            clearInterval(sessionTimerRef.current);
            sessionTimerRef.current = null;
        }
        if (qrRefreshTimerRef.current) {
            clearInterval(qrRefreshTimerRef.current);
            qrRefreshTimerRef.current = null;
        }
    }, []);

    const handleSessionExpired = useCallback(() => {
        stopPolling();
        stopTimers();
        setSessionExpired(true);
        setConsentStatus('expired');
        statusMessageRef.current = t('bankConnStatusSessionExpired');
    }, [stopPolling, stopTimers, t]);

    useEffect(() => {
        setUserAgent(navigator.userAgent);

        fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => { setUserIp(data.ip) })
            .catch(() => setUserIp('127.0.0.1'));

        const countryData = JSON.parse(sessionStorage.getItem('selectedCountry'));
        const bankData = JSON.parse(sessionStorage.getItem('selectedBank'));
        if (countryData && bankData) {
            setBankingInfo({ country: countryData, bank: bankData });
        }

        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
            isPollingRef.current = false;
            if (sessionTimerRef.current) clearInterval(sessionTimerRef.current);
            if (qrRefreshTimerRef.current) clearInterval(qrRefreshTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (bankingInfo?.bank?.bicFi && userIp && userAgent) {
            initializeConsent();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankingInfo, userIp, userAgent]);

    const startSessionTimer = () => {
        setSecondsRemaining(SESSION_DURATION);
        sessionTimerRef.current = setInterval(() => {
            setSecondsRemaining(prev => {
                if (prev <= 1) {
                    handleSessionExpired();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const startQrRefreshTimer = () => {
        setQrCountdown(QR_REFRESH_INTERVAL);
        qrRefreshTimerRef.current = setInterval(() => {
            setQrCountdown(prev => {
                if (prev <= 1) {
                    refreshQrCode();
                    return QR_REFRESH_INTERVAL;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const refreshQrCode = () => {
        const storedConsentData = consentDataRef.current;
        if (!storedConsentData) return;

        // Re-poll authorization to get fresh QR data
        if (storedConsentData.consentId && storedConsentData.authorizationId) {
            checkAuthorizationStatus(storedConsentData.consentId, storedConsentData.authorizationId);
        }
    };

    const setupConsentData = (consentData) => {
        consentDataRef.current = consentData;

        if (consentData.challengeData?.data) {
            generateQRCode(consentData.challengeData.data);
            initialQrSourceRef.current = 'challenge';
        } else if (consentData.scaOAuthLink) {
            generateQRCode(consentData.scaOAuthLink);
            initialQrSourceRef.current = 'oauth';
        } else if (consentData.challengeData?.image) {
            generateQRCode(consentData.challengeData.image);
            initialQrSourceRef.current = 'image';
        }

        if (consentData.scaStatus) {
            scaStatusRef.current = consentData.scaStatus;
        } else {
            scaStatusRef.current = 'started';
        }

        // Start timers
        startSessionTimer();
        startQrRefreshTimer();

        if (consentData.consentId && consentData.authorizationId) {
            setTimeout(() => {
                startPollingAuthorization(consentData.consentId, consentData.authorizationId);
            }, 1500);
        }
    };

    const loadPersistedConsent = () => {
        const storedConsentData = sessionStorage.getItem('consentData');
        if (!storedConsentData) {
            return false;
        }

        try {
            const consentData = JSON.parse(storedConsentData);
            if (consentData?.consentId) {
                setupConsentData(consentData);
                return true;
            }
        } catch (parseError) {
            console.error('Error parsing stored consent data:', parseError);
        }

        return false;
    };

    const createNewConsent = async () => {
        const params = getEnvironmentParams();
        if (!params.token) {
            throw new Error('No authentication token found');
        }

        const headers = {
            'Content-Type': 'application/json',
            'BicFi': bankingInfo?.bank?.bicFi || 'ESSESESS',
            'Psu-Id': bankingInfo?.bank?.socialSecurityNumber,
            'Psu-Ip-Address': userIp,
            'Psu-User-Agent': userAgent,
            'IsSameDevice': 'false',
            'Redirect-Uri': `${envConfig[getCurrentEnv()].redirectUri}`,
            'Authorization': `Bearer ${params.token}`,
            'Tpp-Redirect-Preferred': false,
        };

        const response = await fetch(`${apiUrl}/consents`, {
            method: 'POST',
            headers: headers,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        sessionStorage.setItem('consentData', JSON.stringify(data));
        setupConsentData(data);
    };

    const initializeConsent = async () => {
        if (isCreatingConsentRef.current) {
            return;
        }

        isCreatingConsentRef.current = true;
        setLoading(true);
        setError(null);

        try {
            if (loadPersistedConsent()) {
                setLoading(false);
                return;
            }

            await createNewConsent();
        } catch (err) {
            console.error('Error initializing consent:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            isCreatingConsentRef.current = false;
        }
    };

    const startPollingAuthorization = (consentId, authId) => {
        if (isPollingRef.current) {
            return;
        }
        isPollingRef.current = true;
        pollAuthorization(consentId, authId);
    };

    const pollAuthorization = async (consentId, authId) => {
        await checkAuthorizationStatus(consentId, authId);
        if (isPollingRef.current) {
            pollingTimeoutRef.current = setTimeout(() => {
                pollAuthorization(consentId, authId);
            }, 1500);
        }
    };

    const checkAuthorizationStatus = async (consentId, authId) => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }
            const headers = {
                'Content-Type': 'application/json',
                'BicFi': bankingInfo?.bank?.bicFi || 'ESSESESS',
                'Psu-Id': bankingInfo?.bank?.socialSecurityNumber,
                'Psu-Ip-Address': userIp,
                'Psu-User-Agent': userAgent,
                'Authorization': `Bearer ${params.token}`,
                'Redirect-Uri': `${envConfig[getCurrentEnv()].redirectUri}/bank-connected`,
                'Tpp-Redirect-Preferred': false,
            };
            const response = await fetch(`${apiUrl}/consents/${consentId}/authorization/${authId}`, {
                method: 'GET',
                headers: headers
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const sca = (data?.scaStatus || '').toLowerCase();

            scaStatusRef.current = data?.scaStatus || '';

            if (sca === 'finalised') {
                stopPolling();
                stopTimers();
                startPollingConsent(consentId);
                return;
            }

            if (sca === 'started') {
                if (initialQrSourceRef.current === 'challenge' && data?.challengeData?.data) {
                    generateQRCode(data.challengeData.data);
                }
                if (initialQrSourceRef.current === 'image' && data?.challengeData?.image) {
                    generateQRCode(data.challengeData.image);
                }
            }

            if (['failed', 'rejected', 'denied', 'aborted', 'cancelled', 'expired', 'terminated', 'blocked'].includes(sca)) {
                stopPolling();
                stopTimers();
                setConsentStatus('invalid');
                statusMessageRef.current = t('bankConnStatusAuthFailed');
                return;
            }

            setConsentStatus('authorizing');
            statusMessageRef.current = t('bankConnStatusAuthorizing');
        } catch (err) {
            setError(err.message);
        }
    };

    const startPollingConsent = (consentId) => {
        stopPolling();
        isPollingRef.current = true;
        pollConsent(consentId);
    };

    const pollConsent = async (consentId) => {
        await checkConsentStatus(consentId);
        if (isPollingRef.current) {
            pollingTimeoutRef.current = setTimeout(() => {
                pollConsent(consentId);
            }, 1500);
        }
    };

    const checkConsentStatus = async (consentId) => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }
            const headers = {
                'Content-Type': 'application/json',
                'BicFi': bankingInfo?.bank?.bicFi || 'ESSESESS',
                'Psu-Id': bankingInfo?.bank?.socialSecurityNumber,
                'Psu-Ip-Address': userIp,
                'Psu-User-Agent': userAgent,
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': false,
                'Redirect-Uri': `${envConfig[getCurrentEnv()].redirectUri}`,
            };
            const response = await fetch(`${apiUrl}/consents/${consentId}`, {
                method: 'GET',
                headers: headers
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const status = (typeof data === 'string' ? data : data?.status || '').toLowerCase();

            if (status === 'valid') {
                stopPolling();
                stopTimers();
                setConsentStatus('valid');
                statusMessageRef.current = t('bankConnStatusValid');
                navigate('/bank-connected');
                return;
            }

            if (['rejected', 'failed', 'denied', 'cancelled', 'revoked', 'expired', 'terminated', 'invalid', 'blocked'].includes(status)) {
                stopPolling();
                stopTimers();
                setConsentStatus('invalid');
                statusMessageRef.current = t('bankConnStatusConsentFailed');
                return;
            }

            setConsentStatus('pending');
            statusMessageRef.current = t('bankConnStatusWaiting');
        } catch (err) {
            setError(err.message);
        }
    };

    const generateQRCode = (code) => {
        if (initialQrSourceRef.current === 'image') {
            setQrCodeUrl(code);
        } else {
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(code || '')}`;
            setQrCodeUrl(qrCodeUrl);
        }
    };

    const handleRetry = () => {
        sessionStorage.removeItem('consentData');
        consentDataRef.current = null;
        setSessionExpired(false);
        setConsentStatus('pending');
        setError(null);
        setSecondsRemaining(SESSION_DURATION);
        setQrCountdown(QR_REFRESH_INTERVAL);
        isCreatingConsentRef.current = false;
        initializeConsent();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const renderQRCodeContent = () => {
        if (loading) {
            return <div className="qr-loading">{t('bankConnQrLoading')}</div>;
        }
        if (error) {
            return <div className="qr-error">{t('bankConnQrError')}</div>;
        }
        if (qrCodeUrl) {
            return (
                <img
                    src={qrCodeUrl}
                    alt={t('bankConnQrAlt')}
                    className="qr-code"
                />
            );
        }
        return <div className="qr-placeholder">{t('bankConnQrPlaceholder')}</div>;
    };

    const renderSessionExpired = () => (
        <div className="session-expired-content">
            <div className="session-expired-icon">
                <TimerIcon />
            </div>
            <h2 className="session-expired-title">{t('bankConnSessionExpiredTitle')}</h2>
            <p className="session-expired-desc">{t('bankConnSessionExpiredDesc')}</p>
            <button className="session-expired-retry-btn" onClick={handleRetry}>
                {t('bankConnSessionExpiredRetry')}
            </button>
        </div>
    );

    const navigationProps = {
        previousPath: "/aspspsList",
        nextPath: "/bank-connected",
        previousDisabled: loading,
        nextDisabled: loading || !!error || consentStatus !== 'valid'
    };

    const displayBankName = bankingInfo.bank?.name || bankName;
    const timerProgress = (secondsRemaining / SESSION_DURATION) * 100;

    return (
        <PageLayout currentStep={2} navigationProps={navigationProps}>
            <div className="connection-content">
                {sessionExpired ? renderSessionExpired() : (
                    <>
                        {bankingInfo.bank?.logoUrl && (
                            <div className="bank-logo-container">
                                <img
                                    src={bankingInfo.bank.logoUrl}
                                    alt={displayBankName}
                                    className="bank-connection-logo"
                                />
                            </div>
                        )}

                        <h1 className="main-title">{t('bankConnTitle', { bankName: displayBankName })}</h1>
                        <p className="main-subtitle">{t('bankConnSubtitle')}</p>

                        {/* Session countdown timer - EC-513 */}
                        {!loading && !error && (
                            <div className="session-timer-bar">
                                <div className="session-timer-header">
                                    <div className="session-timer-label">
                                        <TimerIcon />
                                        <span>{t('bankConnSessionTimeRemaining')}</span>
                                    </div>
                                    <span className="session-timer-countdown">{formatTime(secondsRemaining)}</span>
                                </div>
                                <div className="session-timer-track">
                                    <div
                                        className={`session-timer-fill ${secondsRemaining <= 30 ? 'warning' : ''}`}
                                        style={{ width: `${timerProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* QR auto-refresh indicator - EC-512 */}
                        {!loading && !error && qrCodeUrl && (
                            <div className="qr-refresh-indicator">
                                {t('bankConnQrRefreshesIn', { seconds: qrCountdown })}
                            </div>
                        )}

                        <div className="qr-section">
                            <div className="qr-code-container">
                                {renderQRCodeContent()}
                            </div>
                        </div>

                        <div className="instructions-container">
                            <div className="instruction-steps">
                                <div className="instruction-step">
                                    <div className="step-number">1</div>
                                    <span>{t('bankConnStep1')}</span>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">2</div>
                                    <span>{t('bankConnStep2')}</span>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">3</div>
                                    <span>{t('bankConnStep3')}</span>
                                </div>
                                <div className="instruction-step">
                                    <div className="step-number">4</div>
                                    <span>{t('bankConnStep4')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Info bar */}
                        {!loading && !error && (
                            <div className="qr-info-bar">
                                <span className="qr-info-icon">ℹ</span>
                                <span>{t('bankConnQrInfoBar')}</span>
                            </div>
                        )}

                        {error && (
                            <div className="error-message">
                                {t('bankConnErrorMessage')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </PageLayout>
    );
}

export default BankConnection;

BankConnection.propTypes = {
    bankName: PropTypes.string
};
